import { Injectable } from '@angular/core';
import { POSType } from '@digilize/shared/definitions/src';
import { BehaviorSubject, filter } from 'rxjs';

export enum AppTheme {
  Dark = 0,
  Light = 1,
}

export interface AppContextData {
  showId: string;
  shopId: string;
  areaId: string;
  posId: string;
  theme: AppTheme;
  seatsLimit: number;
  orderTimeLimit: number;
  productIds: string[];
  disableSeatsValidation: boolean;
  posType: POSType;
}

@Injectable({
  providedIn: 'root',
})
export class AppContext {
  private data: AppContextData = {
    showId: null,
    shopId: null,
    areaId: null,
    posId: null,
    productIds: null,
    theme: null,
    seatsLimit: null,
    orderTimeLimit: null,
    disableSeatsValidation: null,
    posType: null,
  };

  private readonly posTypeSubject = new BehaviorSubject<POSType | null>(null);

  /**
   * The POS type, once the checkout wrapper has fetched the POS. Nothing is emitted before that, so a
   * view that branches on it renders the right variant instead of flashing the wrong one first.
   */
  readonly posType$ = this.posTypeSubject.pipe(filter((posType): posType is POSType => posType !== null));

  constructor() {}

  get(): AppContextData {
    if (!this.data) {
      throw new Error('App context not set');
    }

    return { ...this.data };
  }

  init(showId: string, areaId: string): void {
    this.data = {
      ...this.data,
      showId,
      areaId,
    };
  }

  setPosId(posId: string): void {
    this.data.posId = posId;
  }
  setProductIds(productIds: string[]): void {
    this.data.productIds = productIds;
  }

  setShowId(showId: string): void {
    this.data.showId = showId;
  }

  setShopId(shopId: string): void {
    this.data.shopId = shopId;
  }

  setPOSType(posType: POSType): void {
    this.data.posType = posType;
    this.posTypeSubject.next(posType);
  }

  setTheme(theme: AppTheme): void {
    this.data.theme = theme;
  }

  setPosContext(
    posId: string,
    theme: AppTheme,
    seatsLimit: number,
    orderTimeLimit: number,
    disableSeatsValidation: boolean
  ): void {
    this.data = {
      ...this.data,
      posId,
      theme,
      seatsLimit,
      orderTimeLimit,
      disableSeatsValidation,
    };
  }

  get posId(): string | never {
    if (!this.data?.posId) {
      throw new Error('POS ID is missing');
    }
    return this.data.posId;
  }

  get showId(): string | never {
    if (!this.data?.showId) {
      throw new Error('Show ID is missing');
    }
    return this.data.showId;
  }

  get productIds(): string[] | never {
    if (!this.data?.productIds) {
      throw new Error('Product Ids are missing');
    }
    return this.data.productIds;
  }

  get shopId(): string | never {
    if (!this.data?.shopId) {
      throw new Error('Shop ID is missing');
    }
    return this.data.shopId;
  }

  get areaId(): string | never {
    if (!this.data?.areaId) {
      throw new Error('Area ID is missing');
    }
    return this.data.areaId;
  }
}
